'use client'

import { Box, Grid } from '@mui/joy'
import ContentCard, { ContentCardProps } from '@components/content/ContentCard'
import { pxToRem } from '@ui/style/muiTheme'
import UIDivider from '@components/ui/UIDivider'

import { theme } from '@ui/style'
import { getRelativeTime } from '@utils/date'
import getBulletColor from '@utils/getBulletColor'
import CSSgridList from '@components/list/CSSgridList'
import { CSSGridListBorder } from '@components/list/CSSgridList/CSSgridListRoot'
import { ArticleItemProps } from '..'
import { getArticleLink } from '@utils/getLink'
import YoutubeContentCard from '@components/content/YoutubeContentCard'

const YoutubeList = ({
	list = [],
	isDark,
	areaIdx,
}: {
	list: Array<ArticleItemProps>
	isDark?: boolean
	areaIdx?: number
}) => {
	const [article1st, ...articleList] = list

	const cardProps: Omit<ContentCardProps, 'content'> = {
		layoutConfig: {
			flexDirection: 'row-reverse',
			layoutGap: pxToRem(16),
			layoutSx: {
				height: {
					lg: pxToRem(68),
					xl: pxToRem(103),
				},
				alignItems: 'flex-start',
			},
		},
		thumbnailConfig: {
			thumbWidth: { xs: pxToRem(120), lg: pxToRem(120), xl: pxToRem(185) },
			thumbHeight: { xs: pxToRem(68), lg: pxToRem(68), xl: pxToRem(103) },
			badgeInset: pxToRem(6),
		},
		infoConfig: {
			isDark,
			infoGap: pxToRem(8),
			infoSx: {
				alignItems: 'flex-start',
				'.info-title': {
					fontSize: {
						xs: pxToRem(16),
						lg: pxToRem(16),
						xl: pxToRem(18),
					},
					lineHeight: {
						xs: pxToRem(22),
						lg: pxToRem(22),
						xl: pxToRem(26),
					},
				},
			},
		},
	}

	const createCardContent = (item: ArticleItemProps, isLargeCard = false) => ({
		articleIdx: item?.articleIdx,
		src: item?.articleThumbnailImgUrl ?? undefined,
		href: getArticleLink(item, 'HOME', areaIdx),
		alt: '',
		title: item.articleTitle,
		additionalText: getRelativeTime(item?.publicationDate ?? '', item?.code === 'LIVE'),
		duration: !isLargeCard ? item?.vodInfo?.playTime : undefined,
		showVideoViewIcon: !isLargeCard ? item?.code !== 'LIVE' && item?.isVideoView : false,
		bullet: item?.bulletType && item.bulletType.name,
		bulletColor: item?.bulletType && getBulletColor(item.bulletType.name),
		showLiveBadge: item?.code === 'LIVE',
		liveTagTypeCode: item?.liveTagTypeCode,
		vodInfo: item?.vodInfo,
	})
	const drawArticleItem = (item: ArticleItemProps, index: number) =>
		index < 4 && (
			<Box
				key={`${item.code ?? 'article'}-item-${item.articleIdx}`}
				sx={{
					'.thumb': {
						display: { xs: 'block', md: 'none', lg: 'block' },
					},
				}}
			>
				<ContentCard
					content={createCardContent(item)}
					layoutConfig={cardProps.layoutConfig}
					thumbnailConfig={cardProps.thumbnailConfig}
					infoConfig={cardProps.infoConfig}
				/>
			</Box>
		)

	return (
		<Grid
			container
			columnSpacing={{ xs: 0, md: pxToRem(24), lg: pxToRem(32) }}
			rowSpacing={{ xs: pxToRem(16), md: 0, lg: 0 }}
			alignItems="flex-start"
			className="list-LGimageList"
		>
			<Grid
				xs={12}
				md={8}
				lg={7.4}
				xl={7}
				display={{ xs: 'block', md: 'flex' }}
				alignItems="stretch"
			>
				{article1st && <YoutubeContentCard article1st={article1st} isDark={isDark} />}
			</Grid>
			<UIDivider
				orientation="vertical"
				sx={{ display: { xs: 'none', md: 'block', lg: 'block' } }}
				isDark={isDark}
			/>
			<Grid xs={12} md={4} lg={4.6} xl={5}>
				<CSSgridList
					gridTemplateRows="repeat(4, 1fr)"
					gridTemplateColumns="1fr"
					gridAutoFlow={{ xs: 'row' }}
					my={{ lg: `${pxToRem(-16)}!important` }}
					isDark={isDark}
					sx={{
						[theme.breakpoints.down('md')]: {
							...CSSGridListBorder('both', isDark),
						},
						[theme.breakpoints.up('md')]: {
							...CSSGridListBorder('both', isDark),
						},
					}}
				>
					{articleList?.map(drawArticleItem)}
				</CSSgridList>
			</Grid>
		</Grid>
	)
}

export default YoutubeList
